import { useState, useEffect, useCallback } from 'react';
import { api, type AdminAnalyticsHealth, type AnalyticsPeriod } from '@/lib/api';
import { PeriodSelector } from './components/PeriodSelector';
import { StackedAreaChart } from './components/StackedAreaChart';
import { TimeSeriesChart } from './components/TimeSeriesChart';
import { BreakdownTable } from './components/BreakdownTable';

interface HealthTabProps {
  /** Optional callback when an admin clicks a userId — drills into the Users tab. */
  onDrillToUser?: (userId: string) => void;
}

export function HealthTab({ onDrillToUser }: HealthTabProps) {
  const [period, setPeriod] = useState<AnalyticsPeriod>('30d');
  const [data, setData] = useState<AdminAnalyticsHealth | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(
    async (nocache = false) => {
      setLoading(true);
      setError(null);
      try {
        const result = await api.adminGetAnalyticsHealth(period, nocache);
        setData(result);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load health');
      } finally {
        setLoading(false);
      }
    },
    [period],
  );

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <PeriodSelector
          period={period}
          onChange={setPeriod}
          onRefresh={() => fetchData(true)}
          loading={loading}
        />
      </div>

      {error && (
        <div className="mb-6 p-4 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive text-sm">
          {error}
        </div>
      )}

      {data && (
        <>
          <div className="bg-card rounded-lg border border-border p-6 mb-6">
            <h2 className="text-lg font-semibold text-foreground mb-2">Entity Health</h2>
            <p className="text-xs text-muted-foreground mb-4">
              Sorted by failure rate (worst first), then by invocation count. p95 latency from
              entity_metrics.
            </p>
            <BreakdownTable
              rows={data.entityHealth}
              columns={[
                { key: 'entityName', label: 'Entity' },
                { key: 'invocations', label: 'Invocations', align: 'right' },
                {
                  key: 'successRate',
                  label: 'Success',
                  align: 'right',
                  render: (r) => {
                    const ok = r.successRate >= 0.95;
                    return (
                      <span className={ok ? '' : 'text-destructive font-semibold'}>
                        {(r.successRate * 100).toFixed(1)}%
                      </span>
                    );
                  },
                },
                {
                  key: 'p95LatencyMs',
                  label: 'p95',
                  align: 'right',
                  render: (r) => `${r.p95LatencyMs} ms`,
                },
              ]}
            />
          </div>

          <div className="bg-card rounded-lg border border-border p-6 mb-6">
            <h2 className="text-lg font-semibold text-foreground mb-2">Daily Invocation Health</h2>
            <StackedAreaChart
              data={data.dailyInvocationHealth}
              series={[
                { dataKey: 'success', label: 'Success', color: '#22c55e' },
                { dataKey: 'failure', label: 'Failure', color: '#ef4444' },
              ]}
            />
          </div>

          <div className="bg-card rounded-lg border border-border p-6 mb-6">
            <h2 className="text-lg font-semibold text-foreground mb-1">Users Near Burn Cap</h2>
            <p className="text-xs text-muted-foreground mb-4">
              Daily cap is{' '}
              <span className="font-mono">{data.dailyBurnCapCredits.toLocaleString()}</span> credits
              ({`$${(data.dailyBurnCapCredits / 100).toFixed(2)}`}). Top 20 users where any single
              day in the period exceeded 80% of cap.
            </p>
            <BreakdownTable
              rows={data.usersNearBurnCap}
              emptyMessage="No users near the daily burn cap in this period."
              columns={[
                {
                  key: 'userId',
                  label: 'User',
                  render: (r) =>
                    onDrillToUser ? (
                      <button
                        onClick={() => onDrillToUser(r.userId)}
                        className="text-primary hover:underline font-mono text-xs"
                      >
                        {r.userId}
                      </button>
                    ) : (
                      <span className="font-mono text-xs">{r.userId}</span>
                    ),
                },
                { key: 'peakDay', label: 'Peak day' },
                {
                  key: 'peakSpendCredits',
                  label: 'Peak spend',
                  align: 'right',
                  render: (r) => `$${(r.peakSpendCredits / 100).toFixed(2)}`,
                },
                { key: 'daysOverEighty', label: 'Days over 80%', align: 'right' },
              ]}
            />
          </div>

          <div className="bg-card rounded-lg border border-border p-6 mb-6">
            <h2 className="text-lg font-semibold text-foreground mb-2">System Bucket by Feature</h2>
            <p className="text-xs text-muted-foreground mb-4">
              How the platform-paid system credits are being consumed. Watch for unexpected features
              pulling hard on this bucket.
            </p>
            <BreakdownTable
              rows={data.systemBucketByFeature}
              columns={[
                { key: 'feature', label: 'Feature' },
                { key: 'count', label: 'Count', align: 'right' },
                {
                  key: 'totalCredits',
                  label: 'Spend',
                  align: 'right',
                  render: (r) => `$${(r.totalCredits / 100).toFixed(2)}`,
                },
              ]}
            />
          </div>

          <div className="bg-card rounded-lg border border-border p-6">
            <h2 className="text-lg font-semibold text-foreground mb-2">
              Analytics Ingestion Health
            </h2>
            <p className="text-xs text-muted-foreground mb-4">
              Events received per hour over the last 24 hours (server clock). A sudden drop means
              the tracker is broken — investigate.
            </p>
            <TimeSeriesChart
              data={data.analyticsIngestionHealth.hourly}
              xKey="hour"
              series={[{ dataKey: 'eventCount', label: 'Events / hour', color: '#8b5cf6' }]}
            />
          </div>
        </>
      )}
    </div>
  );
}
